import { useState, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { DEFAULT_AVATAR_CONFIG } from "./avatarBuilderConfig";

// ─── All valid DiceBear avataaars v9 option values ────────────────────────────

export const HAIR_VALUES = [
  "shortFlat", "shortRound", "shortCurly", "shortWaved", "sides",
  "theCaesar", "theCaesarAndSidePart", "shavedSides",
  "shaggy", "shaggyMullet", "frizzle",
  "straight01", "straight02", "straightAndStrand",
  "bob", "bun", "curly", "curvy", "miaWallace", "longButNotTooLong",
  "fro", "froBand", "bigHair", "dreads", "dreads01", "dreads02", "frida",
  "hat", "winterHat1", "winterHat02", "winterHat03", "winterHat04",
  "hijab", "turban",
];

export const EYE_VALUES = [
  "default", "happy", "side", "squint", "surprised", "wink",
  "hearts", "eyeRoll", "winkWacky", "xDizzy", "closed", "cry",
];

export const EYEBROW_VALUES = [
  "default", "raisedExcited", "upDown", "angry", "flatNatural",
  "frownNatural", "defaultNatural", "angryNatural", "upDownNatural",
  "raisedExcitedNatural", "sadConcerned", "sadConcernedNatural", "unibrowNatural",
];

export const MOUTH_VALUES = [
  "smile", "default", "serious", "twinkle", "eating", "tongue",
  "concerned", "disbelief", "grimace", "sad", "screamOpen", "vomit",
];

export const ACCESSORY_OPTIONS = [
  { label: "None",           accessories: "blank",          accessoriesProbability: 0 },
  { label: "Round",          accessories: "round",          accessoriesProbability: 100 },
  { label: "Prescription 1", accessories: "prescription01", accessoriesProbability: 100 },
  { label: "Prescription 2", accessories: "prescription02", accessoriesProbability: 100 },
  { label: "Sunglasses",     accessories: "sunglasses",     accessoriesProbability: 100 },
  { label: "Wayfarers",      accessories: "wayfarers",      accessoriesProbability: 100 },
  { label: "Kurt",           accessories: "kurt",           accessoriesProbability: 100 },
  { label: "Eyepatch",       accessories: "eyepatch",       accessoriesProbability: 100 },
];

export const FACIAL_HAIR_OPTIONS = [
  { label: "None",      facialHair: "blank",          facialHairProbability: 0 },
  { label: "Light",     facialHair: "beardLight",     facialHairProbability: 100 },
  { label: "Medium",    facialHair: "beardMedium",    facialHairProbability: 100 },
  { label: "Majestic",  facialHair: "beardMajestic",  facialHairProbability: 100 },
  { label: "Moustache", facialHair: "moustacheFancy", facialHairProbability: 100 },
  { label: "Magnum",    facialHair: "moustacheMagnum",facialHairProbability: 100 },
];

export const CLOTHING_VALUES = [
  "hoodie", "shirtCrewNeck", "shirtScoopNeck", "shirtVNeck",
  "graphicShirt", "collarAndSweater", "blazerAndShirt", "blazerAndSweater", "overall",
];

const HAT_TOPS = new Set(["hat","hijab","turban","winterHat1","winterHat02","winterHat03","winterHat04"]);

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_OPTIONS = {
  seed: "default",
  top: "shortFlat",
  hairColor: "2c1b18",
  hatColor: "262e33",
  skinColor: "d08b5b",
  eyes: "default",
  eyebrows: "default",
  mouth: "smile",
  accessories: "blank",
  accessoriesColor: "262e33",
  accessoriesProbability: 0,
  facialHair: "blank",
  facialHairColor: "2c1b18",
  facialHairProbability: 0,
  clothing: "hoodie",
  clothesColor: "54c9e8",
  backgroundColor: "b6e3f4",
};

// Neutral base for all option preview thumbnails — white background so features
// render on a clean surface instead of the user's current avatar.
const PREVIEW_BASE = { ...DEFAULT_OPTIONS, seed: "preview-neutral", backgroundColor: "ffffff" };

// ─── Pure Helpers ─────────────────────────────────────────────────────────────

export function buildAvatarUrl(opts) {
  const o = { ...DEFAULT_OPTIONS, ...opts };
  const params = new URLSearchParams({
    seed: o.seed,
    top: o.top,
    hairColor: o.hairColor,
    hatColor: o.hatColor,
    skinColor: o.skinColor,
    eyes: o.eyes,
    eyebrows: o.eyebrows,
    mouth: o.mouth,
    accessoriesProbability: String(o.accessoriesProbability),
    facialHairProbability: String(o.facialHairProbability),
    clothing: o.clothing,
    clothesColor: o.clothesColor,
    backgroundColor: o.backgroundColor,
    backgroundType: "solid",
  });
  if (o.accessoriesProbability > 0 && o.accessories !== "blank") {
    params.set("accessories", o.accessories);
    params.set("accessoriesColor", o.accessoriesColor);
  }
  if (o.facialHairProbability > 0 && o.facialHair !== "blank") {
    params.set("facialHair", o.facialHair);
    params.set("facialHairColor", o.facialHairColor);
  }
  return `https://api.dicebear.com/9.x/avataaars/svg?${params.toString()}`;
}

export function parseAvatarOptions(url) {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("dicebear.com")) return null;
    const g = (k) => u.searchParams.get(k);
    return {
      seed:                   g("seed")                   || DEFAULT_OPTIONS.seed,
      top:                    g("top")                    || DEFAULT_OPTIONS.top,
      hairColor:              g("hairColor")              || DEFAULT_OPTIONS.hairColor,
      hatColor:               g("hatColor")               || DEFAULT_OPTIONS.hatColor,
      skinColor:              g("skinColor")              || DEFAULT_OPTIONS.skinColor,
      eyes:                   g("eyes")                   || DEFAULT_OPTIONS.eyes,
      eyebrows:               g("eyebrows")               || DEFAULT_OPTIONS.eyebrows,
      mouth:                  g("mouth")                  || DEFAULT_OPTIONS.mouth,
      accessories:            g("accessories")            || DEFAULT_OPTIONS.accessories,
      accessoriesColor:       g("accessoriesColor")       || DEFAULT_OPTIONS.accessoriesColor,
      accessoriesProbability: Number(g("accessoriesProbability") ?? 0),
      facialHair:             g("facialHair")             || DEFAULT_OPTIONS.facialHair,
      facialHairColor:        g("facialHairColor")        || DEFAULT_OPTIONS.facialHairColor,
      facialHairProbability:  Number(g("facialHairProbability") ?? 0),
      clothing:               g("clothing")               || DEFAULT_OPTIONS.clothing,
      clothesColor:           g("clothesColor")           || DEFAULT_OPTIONS.clothesColor,
      backgroundColor:        g("backgroundColor")        || DEFAULT_OPTIONS.backgroundColor,
    };
  } catch {
    return null;
  }
}

// Computes the default avatar options from an admin config + a seed string.
// Used both by the builder's initial state and the admin "reset all" action.
export function getDefaultAvatarOpts(cfg, seed) {
  const getEnabled = (sectionKey) =>
    [...(cfg.sections[sectionKey]?.options ?? [])]
      .filter((o) => o.enabled)
      .sort((a, b) => a.order - b.order);

  const getColors = (groupKey) =>
    (cfg.colors?.[groupKey]?.options ?? [])
      .filter((c) => c.enabled)
      .sort((a, b) => a.order - b.order);

  const defaultOpt = (sectionKey) => {
    const section = cfg.sections[sectionKey];
    const enabled = getEnabled(sectionKey);
    if (section?.defaultId) {
      const starred = enabled.find((o) => o.id === section.defaultId);
      if (starred) return starred;
    }
    return enabled[0] ?? null;
  };

  const firstColor = (groupKey) => getColors(groupKey)[0]?.id ?? null;

  const h  = defaultOpt("hair");
  const e  = defaultOpt("eyes");
  const eb = defaultOpt("eyebrows");
  const m  = defaultOpt("mouth");
  const a  = defaultOpt("accessories");
  const fh = defaultOpt("facialHair");
  const cl = defaultOpt("clothing");

  return {
    ...DEFAULT_OPTIONS,
    seed: seed || "default",
    ...(h  && { top: h.id }),
    ...(e  && { eyes: e.id }),
    ...(eb && { eyebrows: eb.id }),
    ...(m  && { mouth: m.id }),
    ...(a  && { accessories: a.accessories ?? "blank", accessoriesProbability: a.accessoriesProbability ?? 0 }),
    ...(fh && { facialHair: fh.facialHair ?? "blank", facialHairProbability: fh.facialHairProbability ?? 0 }),
    ...(cl && { clothing: cl.id }),
    ...(firstColor("hairColors")     && { hairColor:       firstColor("hairColors") }),
    ...(firstColor("skinTones")      && { skinColor:       firstColor("skinTones") }),
    ...(firstColor("clothingColors") && { clothesColor:    firstColor("clothingColors"), hatColor: firstColor("clothingColors") }),
    ...(firstColor("bgColors")       && { backgroundColor: firstColor("bgColors") }),
  };
}

// ─── Crop regions — SVG bounding boxes within the 280×280 avataaars viewBox ───
// w sets the rendered button width in px; height is computed from the aspect ratio.

export const REGIONS = {
  hair:        { x1:   0, y1:   0, x2: 280, y2: 205, w: 72  },
  eyes:        { x1:  40, y1:  85, x2: 240, y2: 152, w: 110 },
  eyebrows:    { x1:  40, y1:  68, x2: 240, y2: 132, w: 110 },
  mouth:       { x1:  55, y1: 140, x2: 225, y2: 210, w: 88  },
  accessories: { x1:  20, y1:  82, x2: 260, y2: 162, w: 88  },
  facialHair:  { x1:  40, y1: 155, x2: 240, y2: 240, w: 88  },
  clothing:    { x1:   0, y1: 192, x2: 280, y2: 280, w: 84  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const scrollRowStyle = {
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  paddingBottom: "6px",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
};

const labelStyle = {
  fontSize: "10px",
  fontWeight: "700",
  color: "#94a3b8",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: "8px",
  marginTop: "18px",
};

// Preserves horizontal scroll position across re-renders caused by option selection.
function ScrollRow({ children, style }) {
  const ref = useRef(null);
  const savedScroll = useRef(0);
  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollLeft = savedScroll.current;
  });
  return (
    <div
      ref={ref}
      onScroll={(e) => { savedScroll.current = e.currentTarget.scrollLeft; }}
      style={{ ...scrollRowStyle, ...style }}
    >
      {children}
    </div>
  );
}

// Cropped avatar button — rectangular, sized to show only the relevant feature
function CropBtn({ sectionKey, url, selected, onClick }) {
  const r = REGIONS[sectionKey];
  const svgW = r.x2 - r.x1;
  const svgH = r.y2 - r.y1;
  const scale = r.w / svgW;
  const W = r.w;
  const H = Math.round(svgH * scale);
  const imgSize = Math.round(280 * scale);
  const left = Math.round(-r.x1 * scale);
  const top  = Math.round(-r.y1 * scale);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flexShrink: 0,
        width: W,
        height: H,
        borderRadius: "10px",
        overflow: "hidden",
        cursor: "pointer",
        border: selected ? "2.5px solid #2563eb" : "2px solid #e2e8f0",
        padding: 0,
        position: "relative",
        boxShadow: selected ? "0 0 0 3px rgba(37,99,235,0.22)" : "none",
        transition: "border 0.1s, box-shadow 0.1s",
      }}
    >
      <img
        src={url}
        width={imgSize}
        height={imgSize}
        loading="lazy"
        style={{ position: "absolute", left, top, display: "block", pointerEvents: "none" }}
      />
    </button>
  );
}

// Text chip button — used when admin sets section displayMode to "names"
function ChipBtn({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flexShrink: 0,
        padding: "7px 14px",
        borderRadius: "20px",
        border: selected ? "2px solid #2563eb" : "1.5px solid #bfdbfe",
        background: selected ? "#dbeafe" : "#f5faff",
        color: selected ? "#1d4ed8" : "#4b6b8a",
        fontWeight: selected ? "bold" : "normal",
        fontSize: "13px",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

// Color swatch button
function Swatch({ hex, selected, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      style={{
        flexShrink: 0,
        width: 34,
        height: 34,
        borderRadius: "50%",
        background: `#${hex}`,
        border: "2px solid rgba(0,0,0,0.1)",
        cursor: "pointer",
        outline: selected ? "3px solid #2563eb" : "none",
        outlineOffset: "2px",
      }}
    />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AvatarBuilder({ initialUrl, seedHint, onSave, onCancel, config = null }) {
  const cfg = config || DEFAULT_AVATAR_CONFIG;

  // Returns sorted, enabled options for a section from config
  const getOptsFromCfg = (sectionKey) =>
    [...(cfg.sections[sectionKey]?.options ?? [])]
      .filter((o) => o.enabled)
      .sort((a, b) => a.order - b.order);

  // Returns enabled colors for a color group — falls back to hardcoded if no config
  const getColors = (groupKey) =>
    (cfg.colors?.[groupKey]?.options ?? [])
      .filter((c) => c.enabled)
      .sort((a, b) => a.order - b.order);

  const [opts, setOpts] = useState(() => {
    const parsed = initialUrl ? parseAvatarOptions(initialUrl) : null;
    if (parsed) return parsed;

    return getDefaultAvatarOpts(cfg, seedHint || "default");
  });

  const set = (k, v) => setOpts((p) => ({ ...p, [k]: v }));
  const previewUrl = buildAvatarUrl(opts);
  const isHat = HAT_TOPS.has(opts.top);

  const getOpts = getOptsFromCfg;

  // Per-section: icons or text chips
  const sectionIsIcons = (sectionKey) => {
    const mode = cfg.sections[sectionKey]?.displayMode ?? cfg.displayMode ?? "icons";
    return mode !== "names";
  };

  const Btn = ({ sectionKey, url, label, selected, onClick }) =>
    sectionIsIcons(sectionKey)
      ? <CropBtn sectionKey={sectionKey} url={url} selected={selected} onClick={onClick} />
      : <ChipBtn label={label} selected={selected} onClick={onClick} />;

  const sheet = (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(30,58,138,0.45)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={onCancel}
    >
      <div
        style={{ background: "#f5faff", borderRadius: "28px 28px 0 0", width: "100%", maxWidth: "520px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 -8px 32px rgba(30,58,138,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* ── Sticky header: always-visible full preview ── */}
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #dbeafe", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "17px" }}>Build Your Avatar</div>
            <button onClick={onCancel} style={{ background: "none", border: "none", fontSize: "24px", color: "#94a3b8", cursor: "pointer", lineHeight: 1, padding: "4px" }}>×</button>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img src={previewUrl} alt="Your avatar" style={{ width: 90, height: 90, borderRadius: "50%", border: "3px solid #bfdbfe", background: "#dbeafe", display: "block" }} />
          </div>
        </div>

        {/* ── Scrollable options ── */}
        <div style={{ overflowY: "auto", flex: 1, padding: "0 20px" }}>

          {/* Hair */}
          <div style={labelStyle}>Hair</div>
          <ScrollRow>
            {getOpts("hair").map((o) => (
              <Btn key={o.id} sectionKey="hair" url={buildAvatarUrl({ ...PREVIEW_BASE, top: o.id })} label={o.label} selected={opts.top === o.id} onClick={() => set("top", o.id)} />
            ))}
          </ScrollRow>

          {/* Hair Color */}
          {!isHat && getColors("hairColors").length > 0 && (
            <>
              <div style={labelStyle}>Hair Color</div>
              <ScrollRow>
                {getColors("hairColors").map((c) => (
                  <Swatch key={c.id} hex={c.id} label={c.label} selected={opts.hairColor === c.id} onClick={() => set("hairColor", c.id)} />
                ))}
              </ScrollRow>
            </>
          )}

          {/* Hat Color */}
          {isHat && getColors("clothingColors").length > 0 && (
            <>
              <div style={labelStyle}>Hat Color</div>
              <ScrollRow>
                {getColors("clothingColors").map((c) => (
                  <Swatch key={c.id} hex={c.id} label={c.label} selected={opts.hatColor === c.id} onClick={() => set("hatColor", c.id)} />
                ))}
              </ScrollRow>
            </>
          )}

          {/* Skin Tone */}
          {getColors("skinTones").length > 0 && (
            <>
              <div style={labelStyle}>Skin Tone</div>
              <ScrollRow>
                {getColors("skinTones").map((c) => (
                  <Swatch key={c.id} hex={c.id} label={c.label} selected={opts.skinColor === c.id} onClick={() => set("skinColor", c.id)} />
                ))}
              </ScrollRow>
            </>
          )}

          {/* Eyes */}
          <div style={labelStyle}>Eyes</div>
          <ScrollRow>
            {getOpts("eyes").map((o) => (
              <Btn key={o.id} sectionKey="eyes" url={buildAvatarUrl({ ...PREVIEW_BASE, eyes: o.id })} label={o.label} selected={opts.eyes === o.id} onClick={() => set("eyes", o.id)} />
            ))}
          </ScrollRow>

          {/* Eyebrows */}
          <div style={labelStyle}>Eyebrows</div>
          <ScrollRow>
            {getOpts("eyebrows").map((o) => (
              <Btn key={o.id} sectionKey="eyebrows" url={buildAvatarUrl({ ...PREVIEW_BASE, eyebrows: o.id })} label={o.label} selected={opts.eyebrows === o.id} onClick={() => set("eyebrows", o.id)} />
            ))}
          </ScrollRow>

          {/* Mouth */}
          <div style={labelStyle}>Mouth</div>
          <ScrollRow>
            {getOpts("mouth").map((o) => (
              <Btn key={o.id} sectionKey="mouth" url={buildAvatarUrl({ ...PREVIEW_BASE, mouth: o.id })} label={o.label} selected={opts.mouth === o.id} onClick={() => set("mouth", o.id)} />
            ))}
          </ScrollRow>

          {/* Accessories */}
          <div style={labelStyle}>Accessories</div>
          <ScrollRow>
            {getOpts("accessories").map((o) => {
              const isNone = o.accessoriesProbability === 0;
              const selected = isNone
                ? opts.accessoriesProbability === 0
                : opts.accessories === o.accessories && opts.accessoriesProbability > 0;
              const previewUrl2 = isNone
                ? buildAvatarUrl({ ...PREVIEW_BASE, accessoriesProbability: 0 })
                : buildAvatarUrl({ ...PREVIEW_BASE, accessories: o.accessories, accessoriesProbability: 100 });
              return (
                <Btn key={o.id} sectionKey="accessories" url={previewUrl2} label={o.label} selected={selected}
                  onClick={() => setOpts((p) => ({ ...p, accessories: o.accessories, accessoriesProbability: o.accessoriesProbability }))} />
              );
            })}
          </ScrollRow>

          {/* Accessory Color */}
          {opts.accessoriesProbability > 0 && getColors("clothingColors").length > 0 && (
            <>
              <div style={labelStyle}>Accessory Color</div>
              <ScrollRow>
                {getColors("clothingColors").map((c) => (
                  <Swatch key={c.id} hex={c.id} label={c.label} selected={opts.accessoriesColor === c.id} onClick={() => set("accessoriesColor", c.id)} />
                ))}
              </ScrollRow>
            </>
          )}

          {/* Facial Hair */}
          <div style={labelStyle}>Facial Hair</div>
          <ScrollRow>
            {getOpts("facialHair").map((o) => {
              const isNone = o.facialHairProbability === 0;
              const selected = isNone
                ? opts.facialHairProbability === 0
                : opts.facialHair === o.facialHair && opts.facialHairProbability > 0;
              const previewUrl3 = isNone
                ? buildAvatarUrl({ ...PREVIEW_BASE, facialHairProbability: 0 })
                : buildAvatarUrl({ ...PREVIEW_BASE, facialHair: o.facialHair, facialHairProbability: 100 });
              return (
                <Btn key={o.id} sectionKey="facialHair" url={previewUrl3} label={o.label} selected={selected}
                  onClick={() => setOpts((p) => ({ ...p, facialHair: o.facialHair, facialHairProbability: o.facialHairProbability }))} />
              );
            })}
          </ScrollRow>

          {/* Facial Hair Color */}
          {opts.facialHairProbability > 0 && getColors("hairColors").length > 0 && (
            <>
              <div style={labelStyle}>Beard / Moustache Color</div>
              <ScrollRow>
                {getColors("hairColors").map((c) => (
                  <Swatch key={c.id} hex={c.id} label={c.label} selected={opts.facialHairColor === c.id} onClick={() => set("facialHairColor", c.id)} />
                ))}
              </ScrollRow>
            </>
          )}

          {/* Clothing */}
          <div style={labelStyle}>Clothing</div>
          <ScrollRow>
            {getOpts("clothing").map((o) => (
              <Btn key={o.id} sectionKey="clothing" url={buildAvatarUrl({ ...PREVIEW_BASE, clothing: o.id })} label={o.label} selected={opts.clothing === o.id} onClick={() => set("clothing", o.id)} />
            ))}
          </ScrollRow>

          {/* Clothing Color */}
          {getColors("clothingColors").length > 0 && (
            <>
              <div style={labelStyle}>Clothing Color</div>
              <ScrollRow>
                {getColors("clothingColors").map((c) => (
                  <Swatch key={c.id} hex={c.id} label={c.label} selected={opts.clothesColor === c.id} onClick={() => set("clothesColor", c.id)} />
                ))}
              </ScrollRow>
            </>
          )}

          {/* Background Color */}
          {getColors("bgColors").length > 0 && (
            <>
              <div style={labelStyle}>Background</div>
              <ScrollRow style={{ marginBottom: 0 }}>
                {getColors("bgColors").map((c) => (
                  <Swatch key={c.id} hex={c.id} label={c.label} selected={opts.backgroundColor === c.id} onClick={() => set("backgroundColor", c.id)} />
                ))}
              </ScrollRow>
            </>
          )}

          <div style={{ height: "20px" }} />
        </div>

        {/* ── Sticky footer ── */}
        <div style={{ padding: "12px 20px 32px", borderTop: "1px solid #dbeafe", flexShrink: 0, display: "flex", gap: "10px" }}>
          <button type="button" onClick={onCancel}
            style={{ flex: 1, padding: "14px", borderRadius: "16px", border: "2px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontWeight: "bold", fontSize: "15px", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="button" onClick={() => onSave(previewUrl)}
            style={{ flex: 2, padding: "14px", borderRadius: "16px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "15px", cursor: "pointer", boxShadow: "0 4px 14px rgba(37,99,235,0.35)" }}>
            Save Avatar
          </button>
        </div>

      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
